import React, { createContext, useContext, useState } from 'react';

export interface ChatConversation {
  id: number;
  name: string;
  avatar: string;
  preview?: string;
  status?: string;
  conversationId?: string; // UUID for portal conversations
}

interface ChatContextValue {
  openChat: (conversation: ChatConversation) => void;
  openChatWindows: ChatConversation[];
  minimizedChats: ChatConversation[];
  closeChat: (id: number) => void;
  minimizeChat: (id: number) => void;
  closeMinimizedChat: (id: number) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [openChatWindows, setOpenChatWindows] = useState<ChatConversation[]>([]);
  const [minimizedChats, setMinimizedChats] = useState<ChatConversation[]>([]);

  const openChat = (conversation: ChatConversation) => {
    // Restore from minimized
    const isMinimized = minimizedChats.find(c => c.id === conversation.id);
    if (isMinimized) {
      setMinimizedChats(prev => prev.filter(c => c.id !== conversation.id));
      setOpenChatWindows(prev => {
        const updated = prev.length >= 3 ? prev.slice(1) : [...prev];
        return [...updated, isMinimized];
      });
      return;
    }

    // Already open — no-op
    if (openChatWindows.find(c => c.id === conversation.id)) return;

    // Open new window, max 3
    setOpenChatWindows(prev => {
      const updated = prev.length >= 3 ? prev.slice(1) : [...prev];
      return [...updated, conversation];
    });
  };

  const closeChat = (id: number) => {
    setOpenChatWindows(prev => prev.filter(c => c.id !== id));
  };

  const minimizeChat = (id: number) => {
    const chat = openChatWindows.find(c => c.id === id);
    if (chat) {
      setOpenChatWindows(prev => prev.filter(c => c.id !== id));
      setMinimizedChats(prev => [...prev, chat]);
    }
  };

  const closeMinimizedChat = (id: number) => {
    setMinimizedChats(prev => prev.filter(c => c.id !== id));
  };

  return (
    <ChatContext.Provider value={{ openChat, openChatWindows, minimizedChats, closeChat, minimizeChat, closeMinimizedChat }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
